// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Flex,
  Heading,
  IconCheckMarkLine,
  IconMiniArrowEndLine,
  IconMiniArrowStartLine,
  IconQuestionLine,
  IconRefreshLine,
  Spinner,
  Text,
  View,
} from '@instructure/ui';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import QuestionItemComponent from './component/questionItem/QuestionItem';
import ModalFillAccessCodeComponent from './component/ModalFillAccessCodeComponent';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { formatDateTimeLabel, getTimeElapsed, getTimeRemaining } from '~/utils/helper/common';
import { IAnswerQuestion, IQuizQuestion } from '~/utils/interface/quiz';
import { answerFileQuestion, answerQuestion, completeQuiz, getDetailQuiz, startQuiz } from '~/thunks/quiz/quizThunk';
import { QuizSubmissionWorkflowStateTypeEnum } from '~/utils/enum/common';
import { studentRoute } from '~/utils/routes/student';
import { MILLISECOND_PER_SECOND, TIME_DEFAULT_LABEL, TIMEOUT_RE_ANSWER_QUESTION } from '~/utils/constants/common';
// Styles, images, icons
import styles from './QuizTaking.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const QuizTaking = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/quiz', 'common/base']);
  const dispatch = useAppDispatch();
  const { courseId, quizId } = useParams();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { quizDetail, isLoading, errorMessage } = useAppSelector((state) => state.quizState);
  //#endregion Selector

  //#region Declare State
  const [accessCode, setAccessCode] = useState<string>();
  const [quizQuestions, setQuizQuestions] = useState<IQuizQuestion[]>([]);
  const [questionLoadingIds, setQuestionLoadingIds] = useState<string[]>([]);
  const [isStartLoading, setIsStartLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isQuizExpire, setIsQuizExpire] = useState(false);
  const [isOpenModalAccessCode, setIsOpenModalAccessCode] = useState(false);
  const [timeLabel, setTimeLabel] = useState<string>(TIME_DEFAULT_LABEL);
  const submissionTakingQuiz = quizDetail?.submissions.find(
    (submission) => submission.workflowState === QuizSubmissionWorkflowStateTypeEnum.UN_TAKEN
  );
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (quizDetail) {
      setQuizQuestions(quizDetail.questions);
      if (quizDetail.oneQuestionAtTime) {
        const lastQuestionIndex = quizDetail.questions.findLastIndex((question) => question.answer);
        if (lastQuestionIndex > 0) {
          setCurrentQuestionIndex(lastQuestionIndex);
        }
      }
    }
  }, [quizDetail]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (submissionTakingQuiz && quizDetail?.hasAccessCode && accessCode) e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [submissionTakingQuiz, quizDetail, accessCode]);

  useEffect(() => {
    if (isQuizExpire) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuizExpire]);

  useEffect(() => {
    let interval: NodeJS.Timer;

    if (quizDetail?.timeLimit && submissionTakingQuiz?.endAt) {
      interval = setInterval(() => {
        setTimeLabel(getTimeRemaining(submissionTakingQuiz.endAt));
        const end = new Date(submissionTakingQuiz?.endAt).getTime();
        const now = Date.now();
        const diff = Math.max(0, end - now);
        if (diff <= 0 && !isQuizExpire) {
          setIsQuizExpire(true);
        }
      }, MILLISECOND_PER_SECOND);
    }

    if (!quizDetail?.timeLimit && submissionTakingQuiz?.startedAt) {
      interval = setInterval(() => {
        setTimeLabel(getTimeElapsed(submissionTakingQuiz?.startedAt));
      }, 1000);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizDetail?.timeLimit, submissionTakingQuiz]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuiz = async () => {
    if (!courseId || !quizId) return;

    await dispatch(getDetailQuiz({ courseId: courseId, quizId: quizId }))
      .unwrap()
      .then((response) => {
        const quizData = response.data;
        const allowedAttempts = quizData?.allowedAttempts ?? 0;
        const totalAttempts = quizData?.submissions?.length ?? 0;
        const isDoingQuiz = quizData.submissions.find(
          (submission) => submission.workflowState === QuizSubmissionWorkflowStateTypeEnum.UN_TAKEN
        );

        if (!isDoingQuiz && ((allowedAttempts >= 0 && totalAttempts >= allowedAttempts) || !quizData.hasAccessCode)) {
          navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.quiz}/${quizId}`, {
            replace: true,
          });
          return;
        }

        if (!accessCode && quizData.hasAccessCode) {
          setIsOpenModalAccessCode(true);
        }
      });
  };

  const handleStartQuiz = async (accessCode?: string) => {
    if (!courseId || !quizId) return;

    setIsOpenModalAccessCode(false);
    setIsStartLoading(true);
    try {
      await dispatch(startQuiz({ courseId: courseId, quizId: quizId, accessCode }))
        .unwrap()
        .then(async () => {
          await handleGetDetailQuiz();
          setIsOpenModalAccessCode(false);
          setAccessCode(accessCode);
        });
    } catch (e) {
      setIsOpenModalAccessCode(true);
    } finally {
      setIsStartLoading(false);
    }
  };

  const handleAnswerQuestion = async (answer: IAnswerQuestion) => {
    if (!courseId || !quizId || !submissionTakingQuiz) return;

    try {
      await dispatch(
        answerQuestion({
          quizId: quizId.toString(),
          attempt: submissionTakingQuiz?.attempt.toString(),
          validationToken: submissionTakingQuiz.validationToken,
          accessCode: accessCode,
          quizSubmissionId: submissionTakingQuiz.id.toString(),
          quizQuestions: [answer],
        })
      ).unwrap();
    } catch (e) {
      setTimeout(() => {
        handleAnswerQuestion(answer);
      }, TIMEOUT_RE_ANSWER_QUESTION);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleChangeAnswer = (questionId: string, answer: any, save?: boolean) => {
    setQuizQuestions((prevState) =>
      prevState.map((question) => (+question.id === +questionId ? { ...question, answer } : question))
    );

    if (save) {
      handleAnswerQuestion({
        id: questionId.toString(),
        answer,
      });
    }
  };

  const handleChangeFileAnswer = async (questionId: string, file: File) => {
    if (!courseId || !quizId || !submissionTakingQuiz) return;

    setQuestionLoadingIds([...questionLoadingIds, questionId]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('courseId', courseId);
      formData.append('quizId', quizId);
      formData.append('quizSubmissionId', submissionTakingQuiz.id.toString());
      formData.append('attempt', submissionTakingQuiz?.attempt.toString());
      formData.append('validationToken', submissionTakingQuiz.validationToken);
      accessCode && formData.append('accessCode', accessCode);
      formData.append(
        'quizQuestion',
        JSON.stringify({
          id: questionId.toString(),
          answer: file.name,
        })
      );

      await dispatch(answerFileQuestion(formData))
        .unwrap()
        .then(() => {
          setQuizQuestions((prevState) =>
            prevState.map((question) => (+question.id === +questionId ? { ...question, answer: file.name } : question))
          );
        });
    } catch (e) {
    } finally {
      setQuestionLoadingIds((prevState) => prevState.filter((id) => id !== questionId));
    }
  };

  const handlePreviousQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const handleNextQuestion = () => {
    if (quizDetail?.cantGoBack) {
      const isConfirm = window.confirm(t('quiz_leave_question_blank'));
      if (!isConfirm) {
        return;
      }
    }

    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const handleClickQuickQuestion = (index: number) => {
    const isClickable = (!quizDetail?.cantGoBack && quizDetail?.oneQuestionAtTime) || !quizDetail?.oneQuestionAtTime;

    if (!isClickable) {
      return;
    }

    if (quizDetail?.oneQuestionAtTime) {
      setCurrentQuestionIndex(index);
      return;
    }

    scrollToQuestion(`question-${quizQuestions[index].id}`);
  };

  const scrollToQuestion = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const remainQuestion = quizQuestions.filter((question) => !question.answer);
    if (remainQuestion.length > 0 && !isQuizExpire) {
      const isConfirm = window.confirm(
        remainQuestion.length > 1
          ? t('quiz_submit_questions_blank_plural', {
              quantity: remainQuestion.length,
            })
          : t('quiz_submit_questions_blank')
      );
      if (!isConfirm) {
        return;
      }
    }

    if (!courseId || !quizId || !submissionTakingQuiz) return;

    setIsSubmitLoading(true);
    try {
      await dispatch(
        completeQuiz({
          courseId: courseId,
          quizId: quizId.toString(),
          attempt: submissionTakingQuiz?.attempt.toString(),
          validationToken: submissionTakingQuiz.validationToken,
          accessCode: accessCode,
          quizSubmissionId: submissionTakingQuiz.id.toString(),
        })
      )
        .unwrap()
        .then(() => {
          navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.quiz}/${quizId}`, {
            replace: true,
          });
        });
    } catch (e) {
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const isLoadingState = (
    <div className={cx('centerContainer')}>
      <Spinner size='small' />
    </div>
  );

  const errorState = (
    <div className={cx('centerContainer')}>
      <Button onClick={handleGetDetailQuiz} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
        {t('retry_button_label', { ns: 'common/base' })}
      </Button>
    </div>
  );

  const renderQuizDetail = () => {
    if (isLoading || isOpenModalAccessCode || isStartLoading) return isLoadingState;

    if (errorMessage) return errorState;

    const submission = quizDetail?.submissions?.find(
      (submission) => submission.workflowState === QuizSubmissionWorkflowStateTypeEnum.UN_TAKEN
    );

    return (
      <>
        <Text size='large' weight='bold'>
          {quizDetail?.title}
        </Text>

        <View margin='small 0'>
          <Text size='medium'>
            {`${t('quiz_started_label')}: ${formatDateTimeLabel(
              submission?.startedAt ?? '',
              t('common_at_time_label', { ns: 'common/base' }),
              true
            )}`}
          </Text>
        </View>

        <Text size='large' weight='bold'>
          {t('quiz_instructions_label')}
        </Text>

        <div
          className={cx('descriptionContainer')}
          dangerouslySetInnerHTML={{ __html: quizDetail?.description ?? '' }}
        />

        {quizDetail?.locked && !submissionTakingQuiz && (
          <Text variant='contentSmall'>
            {t('quiz_locked_label', {
              time: formatDateTimeLabel(
                quizDetail?.lockDate ?? '',
                t('common_at_time_label', { ns: 'common/base' }),
                true
              ),
            })}
          </Text>
        )}

        {submissionTakingQuiz &&
          (quizDetail?.oneQuestionAtTime ? renderListQuestionsByPagination() : renderQuestionsByList())}
      </>
    );
  };

  const renderQuestionsByList = () => {
    if (!quizQuestions.length) return;

    return (
      <div className={cx('questionListContainer')}>
        {quizQuestions.map((question) => (
          <QuestionItemComponent
            question={question}
            onChangeAnswer={handleChangeAnswer}
            onChangeFileAnswer={handleChangeFileAnswer}
            isLoading={questionLoadingIds.includes(question.id)}
          />
        ))}
      </div>
    );
  };

  const renderListQuestionsByPagination = () => {
    if (!quizQuestions.length) return;

    return (
      <div>
        {quizQuestions[currentQuestionIndex] && (
          <QuestionItemComponent
            question={quizQuestions[currentQuestionIndex]}
            onChangeAnswer={handleChangeAnswer}
            onChangeFileAnswer={handleChangeFileAnswer}
            isLoading={questionLoadingIds.includes(quizQuestions[currentQuestionIndex].id)}
          />
        )}

        <Flex justifyItems='space-between' margin='large 0 0 0'>
          {currentQuestionIndex > 0 && !quizDetail?.cantGoBack ? (
            <Button color='secondary' type='button' onClick={handlePreviousQuestion}>
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <IconMiniArrowStartLine />
                <span>{t('previous_button_label', { ns: 'common/base' })}</span>
              </Flex>
            </Button>
          ) : (
            <div></div>
          )}

          {currentQuestionIndex < quizQuestions.length - 1 && (
            <Button color='primary' type='button' onClick={handleNextQuestion}>
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>{t('next_button_label', { ns: 'common/base' })}</span>
                <IconMiniArrowEndLine />
              </Flex>
            </Button>
          )}
        </Flex>
      </div>
    );
  };

  const renderQuickQuestion = () => {
    if (isLoading || isOpenModalAccessCode || isStartLoading) return isLoadingState;

    if (errorMessage) return errorState;

    if (!quizQuestions.length) {
      return (
        <div className={cx('centerContainer')}>
          <Button onClick={handleGetDetailQuiz} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('quiz_no_data')}
          </Button>
        </div>
      );
    }

    return (
      <>
        <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
          <Heading level='h3'>{t('quiz_question_label')}</Heading>
        </Flex>

        <div className={cx('quickQuestionContainer')}>
          {quizQuestions.map((question, index) => {
            const isClickable =
              (!quizDetail?.cantGoBack && quizDetail?.oneQuestionAtTime) || !quizDetail?.oneQuestionAtTime;
            return (
              <View
                key={index}
                as='div'
                className={isClickable ? cx('quickQuestionItem') : undefined}
                onClick={() => handleClickQuickQuestion(index)}
              >
                <Flex gap='small' margin='0 0 small 0'>
                  {question.answer ? <IconCheckMarkLine /> : <IconQuestionLine />}
                  <Text
                    color='brand'
                    weight={currentQuestionIndex === index && quizDetail?.oneQuestionAtTime ? 'bold' : 'normal'}
                  >
                    {question.questionName}
                  </Text>
                </Flex>
              </View>
            );
          })}
        </div>
      </>
    );
  };

  const renderSubmitSection = () => {
    if (isLoading || isOpenModalAccessCode || isStartLoading) return isLoadingState;

    if (errorMessage) return errorState;

    return (
      <Flex width={'100%'} height={'100%'} direction='column' alignItems='center' justifyItems='center' gap='small'>
        <Text>{quizDetail?.timeLimit ? `Time Running: ${timeLabel}` : `Time Elapsed: ${timeLabel}`}</Text>
        <Button color='primary' type='button' disabled={isSubmitLoading} onClick={handleSubmit}>
          <Flex justifyItems='center' alignItems='center' gap='small'>
            <span>{t('submit_button_label', { ns: 'common/base' })}</span>
            {isSubmitLoading && <Spinner size='x-small' />}
          </Flex>
        </Button>
      </Flex>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('content')}>
        <div className={cx('leftSection')}>{renderQuizDetail()}</div>

        <div className={cx('rightSection')}>
          <div className={cx('rightSectionItem')}>{renderQuickQuestion()}</div>

          <div className={cx('rightSectionItem')}>{renderSubmitSection()}</div>
        </div>
      </div>

      <ModalFillAccessCodeComponent isOpen={isOpenModalAccessCode} onSubmit={handleStartQuiz} />
    </div>
  );
};

export default QuizTaking;
